import { useMemo, useState } from "react";
import { ChevronLeft, ChevronRight, X } from "react-feather";
import { useToast } from "./Toast";
import {
  MEAL_SLOTS,
  MEAL_SLOT_LABEL_KEYS,
  MEAL_SLOT_SHORT_KEYS,
  type MealSlot,
  addPlannedMeal,
  selectWeekStartsOn,
  selectLocale,
  intlLocale,
  useT,
  useAppDispatch,
  useAppSelector,
} from "@reciperoll/core";

const pad = (n: number) => String(n).padStart(2, "0");
const ymd = (d: Date) => `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}`;
const addDays = (d: Date, n: number) => {
  const x = new Date(d);
  x.setDate(x.getDate() + n);
  return x;
};
const startOfWeek = (d: Date, weekStartsOn: number) => {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  x.setDate(x.getDate() - ((x.getDay() - weekStartsOn + 7) % 7));
  return x;
};

// Pick a day + slot and add this recipe to the meal plan.
export function AddToMealPlanModal({
  recipeId,
  recipeTitle,
  onClose,
}: {
  recipeId: string;
  recipeTitle?: string;
  onClose: () => void;
}) {
  const t = useT();
  const dispatch = useAppDispatch();
  const toast = useToast();
  const weekStartsOn = useAppSelector(selectWeekStartsOn);
  const intl = intlLocale(useAppSelector(selectLocale));

  const [weekOffset, setWeekOffset] = useState(0);
  const [selectedDate, setSelectedDate] = useState(() => ymd(new Date()));
  const [slot, setSlot] = useState<MealSlot>("dinner");
  const todayYmd = ymd(new Date());

  const weekStart = useMemo(
    () => startOfWeek(addDays(new Date(), weekOffset * 7), weekStartsOn),
    [weekOffset, weekStartsOn],
  );
  const days = useMemo(
    () => Array.from({ length: 7 }, (_, i) => addDays(weekStart, i)),
    [weekStart],
  );
  const weekLabel = `${days[0].toLocaleDateString(intl, { day: "numeric", month: "short" })} – ${days[6].toLocaleDateString(intl, { day: "numeric", month: "short" })}`;

  const add = () => {
    dispatch(addPlannedMeal({ recipeId, mealDate: selectedDate, slot }));
    const label = new Date(selectedDate + "T00:00:00").toLocaleDateString(intl, {
      weekday: "short",
      day: "numeric",
    });
    toast.show(t("mealPlan.addedToSlot", { slot: t(MEAL_SLOT_SHORT_KEYS[slot]), date: label }));
    onClose();
  };

  return (
    <div
      className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4"
      onClick={onClose}
    >
      <div
        className="w-full max-w-md rounded-2xl bg-surface p-6"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between mb-4">
          <div className="min-w-0">
            <h2 className="text-xl font-bold text-ink">{t("recipe.addToMealPlan")}</h2>
            {recipeTitle && (
              <p className="text-sm text-ink-faint truncate">{recipeTitle}</p>
            )}
          </div>
          <button
            onClick={onClose}
            aria-label={t("common.close")}
            className="shrink-0 w-9 h-9 flex items-center justify-center rounded-lg text-ink-faint hover:text-ink hover:bg-surface-2 cursor-pointer"
          >
            <X className="w-5 h-5" />
          </button>
        </div>

        {/* Week nav */}
        <div className="flex items-center justify-center gap-3 mb-4">
          <button
            onClick={() => setWeekOffset((o) => o - 1)}
            aria-label={t("mealPlan.previousWeek")}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-line text-ink-soft hover:bg-surface-2 cursor-pointer"
          >
            <ChevronLeft className="w-4 h-4" />
          </button>
          <button
            onClick={() => setWeekOffset(0)}
            className="min-w-[120px] text-center cursor-pointer"
          >
            <span className="block text-sm font-bold text-ink">{weekLabel}</span>
            <span className="block text-xs font-semibold text-terracotta-ink">
              {weekOffset === 0 ? t("mealPlan.thisWeek") : t("mealPlan.backToThisWeek")}
            </span>
          </button>
          <button
            onClick={() => setWeekOffset((o) => o + 1)}
            aria-label={t("mealPlan.nextWeek")}
            className="w-8 h-8 flex items-center justify-center rounded-lg border border-line text-ink-soft hover:bg-surface-2 cursor-pointer"
          >
            <ChevronRight className="w-4 h-4" />
          </button>
        </div>

        {/* Day chips */}
        <p className="text-[11px] font-bold uppercase tracking-wider text-ink-faint mb-2">{t("mealPlan.day")}</p>
        <div className="grid grid-cols-7 gap-1.5 mb-5">
          {days.map((day) => {
            const dYmd = ymd(day);
            const active = dYmd === selectedDate;
            const isToday = dYmd === todayYmd;
            return (
              <button
                key={dYmd}
                onClick={() => setSelectedDate(dYmd)}
                className={`flex flex-col items-center rounded-lg py-2 border transition-colors cursor-pointer ${
                  active
                    ? "bg-terracotta border-terracotta text-white"
                    : isToday
                      ? "bg-surface border-terracotta hover:bg-surface-2"
                      : "bg-surface border-line hover:bg-surface-2"
                }`}
              >
                <span
                  className={`text-[10px] font-semibold ${active ? "text-white/80" : isToday ? "text-terracotta-ink" : "text-ink-faint"}`}
                >
                  {day.toLocaleDateString(intl, { weekday: "short" }).slice(0, 3)}
                </span>
                <span
                  className={`text-sm font-bold ${active ? "text-white" : isToday ? "text-terracotta-ink" : "text-ink"}`}
                >
                  {day.getDate()}
                </span>
              </button>
            );
          })}
        </div>

        {/* Slot chips */}
        <p className="text-[11px] font-bold uppercase tracking-wider text-ink-faint mb-2">{t("mealPlan.meal")}</p>
        <div className="grid grid-cols-4 gap-2 mb-6">
          {MEAL_SLOTS.map((s) => {
            const active = s === slot;
            return (
              <button
                key={s}
                onClick={() => setSlot(s)}
                className={`rounded-lg py-2.5 border text-[13px] font-semibold transition-colors cursor-pointer ${
                  active
                    ? "bg-terracotta border-terracotta text-white"
                    : "bg-surface border-line text-ink-soft hover:bg-surface-2"
                }`}
              >
                {t(MEAL_SLOT_SHORT_KEYS[s])}
              </button>
            );
          })}
        </div>

        <button
          onClick={add}
          className="w-full rounded-xl bg-terracotta hover:bg-terracotta-dark transition-colors py-3 text-sm font-bold text-white cursor-pointer"
        >
          {t("mealPlan.addToSlot", { slot: t(MEAL_SLOT_LABEL_KEYS[slot]) })}
        </button>
      </div>
    </div>
  );
}
